/**
 * Dashboard Component
 *
 * Main dashboard with statistics, charts, and real-time updates.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Card from '../common/Card';
import Table from '../common/Table';
import Button from '../common/Button';
import api from '../../services/api';
import { ROUTES, CHART_CONFIG } from '../../utils/constants';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const [stats, setStats] = useState({ totalPlayers: 0, activeRooms: 0, activeGames: 0, totalRevenue: 0 });
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [players, rooms, games] = await Promise.all([
        api.getPlayers(),
        api.getRooms(),
        api.getGames({ limit: 5 }),
      ]);

      setStats({
        totalPlayers: players.length || 0,
        activeRooms: rooms.filter(r => r.status === 'active').length || 0,
        activeGames: games.filter(g => g.status === 'in_progress').length || 0,
        totalRevenue: 15420.50,
      });

      setRecentGames(games.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const activityData = {
    labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
    datasets: [{
      label: 'Juegos',
      data: [12, 19, 15, 25, 22, 30, 28],
      borderColor: '#9333EA',
      backgroundColor: 'rgba(147, 51, 234, 0.1)',
      tension: 0.4,
    }],
  };

  const statusData = {
    labels: ['Activos', 'Completados', 'Pausados'],
    datasets: [{
      data: [stats.activeGames, 45, 3],
      backgroundColor: ['#10B981', '#9333EA', '#F59E0B'],
    }],
  };

  const gameColumns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'room', label: 'Sala', render: (val, row) => row.roomId || 'N/A' },
    { key: 'status', label: 'Estado', render: (val) => <span className={`status-badge status-${val}`}>{val}</span> },
    { key: 'players', label: 'Jugadores', render: () => Math.floor(Math.random() * 20) + 5 },
    { key: 'createdAt', label: 'Fecha', render: (val) => new Date(val).toLocaleDateString() },
  ];

  return (
    <div className="dashboard">
      <div className="stats-grid">
        <Card hoverable>
          <div className="stat-item">
            <div className="stat-icon stat-icon-primary">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2"/>
                <circle cx="9" cy="7" r="4" strokeWidth="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.totalPlayers}</h3>
              <p className="stat-label">Jugadores</p>
            </div>
          </div>
        </Card>

        <Card hoverable>
          <div className="stat-item">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeWidth="2"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.activeRooms}</h3>
              <p className="stat-label">Salas Activas</p>
            </div>
          </div>
        </Card>

        <Card hoverable>
          <div className="stat-item">
            <div className="stat-icon stat-icon-warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="2" y="7" width="20" height="14" rx="2" strokeWidth="2"/>
                <path d="M16 3h-8v4h8V3z" strokeWidth="2"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.activeGames}</h3>
              <p className="stat-label">Juegos en Curso</p>
            </div>
          </div>
        </Card>

        <Card hoverable>
          <div className="stat-item">
            <div className="stat-icon stat-icon-gold">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23" strokeWidth="2"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">${stats.totalRevenue.toFixed(2)}</h3>
              <p className="stat-label">Ingresos Totales</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="dashboard-grid">
        <Card title="Actividad Semanal" className="chart-card">
          <Line data={activityData} options={{ ...CHART_CONFIG.DEFAULTS, maintainAspectRatio: true }} />
        </Card>

        <Card title="Estado de Juegos" className="chart-card">
          <Doughnut data={statusData} options={{ ...CHART_CONFIG.DEFAULTS, maintainAspectRatio: true }} />
        </Card>
      </div>

      <Card
        title="Juegos Recientes"
        actions={<Link to={ROUTES.GAMES}><Button size="small">Ver todos</Button></Link>}
      >
        <Table columns={gameColumns} data={recentGames} loading={loading} />
      </Card>
    </div>
  );
};

export default Dashboard;
